﻿<template>
  <card-canvas
    class="conversation-create" 
    unstick-header 
    size="medium"
  >
    <template #header>
      <item-create-topbar
        :entity-id="mailboxID"
        :entity-name="mailboxName"
        :entity-type="EEntities.MAILBOXES"
      />
    </template>
    <div class="add-convo-form">
      <loading-placeholder v-if="isLoading" :lines="20" />
      <template v-else>
        <div class="add-convo-form__main">
          <!-- Subject -->
          <masthead-input
            v-model="formData.subject"
            :is-focused="focusSubject"
            class="subject"
            placeholder="Enter conversation subject…"
            maxlength="255"
          />
          <!-- To and CC -->
          <to-cc-inputs 
            v-model:to-email="formData.to"
            v-model:ccd-emails="formData.cc"
            :focus-to="focusTo"
            :mailbox-id="mailboxID"
          />
          <!-- Message -->
          <editor
            v-model="formData.message"
            :mention-users="mentionOptions"
            :saved-replies="savedReplyOptions"
            :signature="replySignature"
            allows-attachment
            allows-google-drive
            placeholder="Write a message..."
            @mentions-list="insertMentions" 
            @files-list="addFileHandler"
            @file-list-removed="removeFileHandler"
          />
          <div class="meta-section">
            <!-- Priority / Due -->
            <div>
              <div class="label"><i class="ddi ddi-warning" />Priority</div>
              <priority-picker
                v-model:selected-id="formData.priorityID"
                :options="priorityOptions"
                :size="pickerSize"
              />
              <due-date-picker 
                v-model="formData.dueDate" 
                :size="pickerSize"
              />
            </div>
            <!-- Assignment / Status -->
            <template v-if="hasJobs">
              <div v-for="(jobAppointee, index) in jobInfo.jobAppointees" :key="index">
                <div class="label"><i class="ddi ddi-unassigned" />{{jobAppointee.job.name}}</div>
                <assignment-picker
                  v-model:selected-id="formData.listJobAppointeeMappings[index].appointeeID"
                  :options="assigneesOptions"
                  :size="pickerSize"
                />
              </div>
            </template>
            <div v-else>
              <div class="label"><i class="ddi ddi-unassigned" />Assignee</div>
              <assignment-picker
                v-model:selected-id="formData.assigneeID"
                :options="assigneesOptions"
                :size="pickerSize"
              />
            </div>
            <!-- Status -->
            <div>
              <div class="label"><i class="ddi ddi-workflows" />Status</div>
              <status-picker
                v-model:selected-id="formData.statusID"
                :statuses="statusOptions"
                :job-info="jobInfo"
                :size="pickerSize"
              />
            </div>
            <!-- Tags -->
            <div class="small-pad">
              <div class="label"><i class="ddi ddi-tag" />Tags</div>
              <tags-input
                v-model:selected-tags="formData.listTags"
                :available-tags="tagsOptions"
                hide-icon
                class="tags-input"
              />
            </div>
            <!-- Watching -->
            <div>
              <div class="label"><i class="ddi ddi-watching" />Watchers</div>
              <watchers-picker
                v-model:selected-ids="formData.listWatchersIDs"
                :options="watchersOptions"
                :current-user-id="$permissions.loggedInUser.id"
                :type="EEntities.PROJECTS"
                hide-instructions
                from-drawer
                class="watchers-picker"
              />
            </div>
          </div>
          <!-- Custom fields: their own box below the standard fields (only the ones this user can edit render) -->
          <custom-field-inputs
            ref="customFieldInputs"
            :fields="customFieldOptions"
            :size="pickerSize"
            @input="formData.listCustomFieldValues = $event"
          />
        </div>
        <div class="add-convo-form__footer">
          <dd-button
            :is-disabled="isSubmitting"
            :is-loading="isSubmitting && !pressedCreateAnother"
            :color="ColorAssistant.MailboxColor"
            size="large"
            @click="validateAndSubmit(false)"
          >Send conversation</dd-button>
          <dd-button
            :is-disabled="isSubmitting"
            :is-loading="isSubmitting && pressedCreateAnother"
            size="large"
            type="flat"
            @click="validateAndSubmit(true)"
          >Send &amp; add another</dd-button>
          <dd-button
            :is-disabled="isSubmitting"
            type="text"
            color="grey"
            size="large"
            @click="handleCancelClick()"
          >Cancel</dd-button>
        </div>
        <confirm-reply-variables-modal
          v-if="showReplyVariablesModal"
          v-model:variable-replacements="replyVariableReplacements"
          :has-customer-name="messageHasCustomerName"
          :has-customer-first-name="messageHasCustomerFirstName"
          :has-customer-last-name="messageHasCustomerLastName"
          :create-another="pressedCreateAnother"
          @confirm-canceled="closeReplyVariablesModal"
          @send-clicked="replaceVariablesAndResubmit"
        />
      </template>
    </div>
    <!-- Preventive modal -->
    <div v-if="showCreationDeniedOverlay" class="conversation-create__creation-denied-overlay">
      <div>
        <img
          :src="mailboxesImg"
          :srcset="`${mailboxesImg} 1x, ${mailboxesImg2x} 2x, ${mailboxesImg3x} 3x`"
          alt="Mailbox image"
        >
        <h4>Before you can create a new conversation...</h4>
        <!-- Case: Over rate limit -->
        <template v-if="newConvosCheck.overRateLimit">
          <p>
            You are currently over the <a href="https://www.donedone.com/help-docs/mailbox-email-limits" class="just-a-link" target="_blank">rate limit</a> for
            creating new conversations in this account. You need to wait {{ newConvosCheck.rateLimitExpiryInSeconds }} seconds before
            creating your next conversation.
          </p>
          <div class="actions-container">
            <dd-button
              type="flat"
              size="medium"
              @click="RouteAssistant.goToMailbox($router, $route.params.accountId, mailboxID, mailboxName)"
            >Back to mailbox</dd-button>
          </div>
        </template>
        <!-- Case: Complete full setup -->
        <template v-else-if="newConvosCheck.needsForwardingAddresses && newConvosCheck.needsCustomFromAddress">
          <template v-if="$permissions.isAdminOrOwner">
            <p>
              You need to complete your mailbox set up by verifying a forwarding and outgoing email address.
            </p>
            <div class="actions-container">
              <dd-button
                color="orange"
                size="medium"
                @click="RouteAssistant.goToMailboxSettingsDirectConnectionRoute($router, $route.params.accountId, mailboxID)"
              >Complete mailbox setup</dd-button>
              <dd-button
                type="flat"
                size="medium"
                @click="RouteAssistant.goToMailbox($router, $route.params.accountId, mailboxID, mailboxName)"
              >Not now</dd-button>
            </div>
          </template>
          <template v-else>
            <p>
              An admin needs to complete mailbox set up by verifying a forwarding and outgoing email address.
            </p>
            <div class="actions-container">
              <dd-button
                type="flat"
                size="medium"
                @click="RouteAssistant.goToMailbox($router, $route.params.accountId, mailboxID, mailboxName)"
              >Back to mailbox</dd-button>
            </div>
          </template>
        </template>
        <!-- Case: Complete forwarding only -->
        <template v-else-if="newConvosCheck.needsForwardingAddresses">
          <template v-if="$permissions.isAdminOrOwner">
            <p>
              You need to set your outgoing email address <strong>{{newConvosCheck.fromAddress}}</strong> as a forwarding address.
            </p>
            <div class="actions-container">
              <dd-button
                color="orange"
                size="medium"
                @click="RouteAssistant.goToMailboxSettingsAutoForwardingRoute($router, $route.params.accountId, mailboxID)"
              >Add auto-forwarding</dd-button>
              <dd-button
                type="flat"
                size="medium"
                @click="RouteAssistant.goToMailbox($router, $route.params.accountId, mailboxID, mailboxName)"
              >Not now</dd-button>
            </div>
          </template>
          <template v-else>
            <p>
              An admin needs to set your outgoing email address <strong>{{newConvosCheck.fromAddress}}</strong> as a forwarding address.
            </p>
            <div class="actions-container">
              <dd-button
                type="flat"
                size="medium"
                @click="RouteAssistant.goToMailbox($router, $route.params.accountId, mailboxID, mailboxName)"
              >Back to mailbox</dd-button>
            </div>
          </template>
        </template>
        <!-- Case: Complete custom from only -->
        <template v-else-if="newConvosCheck.needsCustomFromAddress">
          <template v-if="$permissions.isAdminOrOwner">
            <p>
              You need to set up an outgoing email address with a domain you own.
            </p>
            <div class="actions-container">
              <dd-button
                color="orange"
                size="medium"
                @click="RouteAssistant.goToMailboxSettingsOutgoingEmailRoute($router, $route.params.accountId, mailboxID)"
              >Add outgoing email address</dd-button>
              <dd-button
                type="flat"
                size="medium"
                @click="RouteAssistant.goToMailbox($router, $route.params.accountId, mailboxID, mailboxName)"
              >Not now</dd-button>
            </div>
          </template>
          <template v-else>
            <p>
              An admin needs to set up an outgoing email address with a domain you own.
            </p>
            <div class="actions-container">
              <dd-button
                type="flat"
                size="medium"
                @click="RouteAssistant.goToMailbox($router, $route.params.accountId, mailboxID, mailboxName)"
              >Back to mailbox</dd-button>
            </div>
          </template>
        </template>
        <!-- Case: Reply-to != from addy -->
        <template v-else-if="newConvosCheck.needsReplyToAddressToBeCustomFromAddress">
          <template v-if="$permissions.isAdminOrOwner">
            <p>
              Your outgoing email address <strong>{{newConvosCheck.fromAddress}}</strong> must also be verified
              as a forwarding address into this mailbox. 
            </p>
            <div class="actions-container">
              <dd-button
                color="orange"
                size="medium"
                @click="RouteAssistant.goToMailboxSettingsAutoForwardingRoute($router, $route.params.accountId, mailboxID)"
              >Go to auto-forwarding</dd-button>
              <dd-button
                type="flat"
                size="medium"
                @click="RouteAssistant.goToMailbox($router, $route.params.accountId, mailboxID, mailboxName)"
              >Not now</dd-button>
            </div>
          </template>
          <template v-else>
            <p>
              Your outgoing email address <strong>{{newConvosCheck.fromAddress}}</strong> must also be verified
              as a forwarding address into this mailbox. Contact an admin to update this.
            </p>
            <div class="actions-container">
              <dd-button
                type="flat"
                size="medium"
                @click="RouteAssistant.goToMailbox($router, $route.params.accountId, mailboxID, mailboxName)"
              >Back to mailbox</dd-button>
            </div>
          </template>
        </template>
        <!-- Case: Catch-all -->
        <template v-else>
          <p>
            You cannot create new conversations at this time.
          </p>
          <div class="actions-container">
            <dd-button
              type="flat"
              size="medium"
              @click="RouteAssistant.goToMailbox($router, $route.params.accountId, mailboxID, mailboxName)"
            >Back to mailbox</dd-button>
          </div>
          <p class="help-notice">
            Need help? <a href="https://www.donedone.com/help-center" target="_blank" class="just-a-link">Contact us for assistance</a>.
          </p>
        </template>
      </div>
    </div>
  </card-canvas>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import DefaultLayout from '@/layouts/default-layout.vue'
import MainContainer from '@/layouts/main-container.vue'
import EntityChildLayout from '@/layouts/entity-child-layout.vue'

/* Mixins */

/* Components */
import CardCanvas from '@/layouts/card-canvas.vue'

import mailboxesImg from '@/assets/img/entities/illustration-mailbox-empty-state.png'
import mailboxesImg2x from '@/assets/img/entities/illustration-mailbox-empty-state@2x.png'
import mailboxesImg3x from '@/assets/img/entities/illustration-mailbox-empty-state@3x.png'

/* Helpers */
import LocalStore from '@/helpers/localstore'
import RouteAssistant from '@/helpers/route-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { EEntities } from '@/types'
import { IAttachment } from '@/types/data-objects/attachments'
import { IValidationResult } from '@/types/data-objects/global'
import {
  IAtMentionUser,
  IConvertedSavedReply,
  ICustomField,
  IConvosOutputReplyVariableReplacements,
  IPersonWithEmail,
  IPriorityLevelForSelection,
  IStatusForSelection,
  ICreateNewConversationsStatus,
  IJob,
  IJobAssigneeMandate,
} from '@/api/types/response.types'
import { ICreateItemStorage, IJobInfo } from '@/types/data-objects/items'
import { IConvosInputCreate } from '@/api/types/request.types'

/* API */
import FilesAPI from '@/api/modules/files.api'
import MailboxesAPI from '@/api/modules/mailboxes.api'
import ConversationsAPI from '@/api/modules/convos.api'

export default defineComponent({
  name: 'ConversationCreate',

  components: {
    CardCanvas,
    DefaultLayout,
    MainContainer,
    EntityChildLayout,
  },

  data() {
    return {
      EEntities,
      ColorAssistant,
      RouteAssistant,
      mailboxesImg,
      mailboxesImg2x,
      mailboxesImg3x,

      FilesAPI,
      browser: this.$deviceInfo.getBrowser,
      mailboxName: '',
      isDirectConnection: false,

      // Create new convos check
      newConvosCheck: {} as ICreateNewConversationsStatus,

      // UI controls
      isSubmitting: false,
      pressedCreateAnother: false,

      // Focus controls
      focusSubject: true,
      focusTo: false,

      // Form
      formData: {
        subject: '',
        to: '',
        cc: [],
        message: '',
        assigneeID: null,
        statusID: null,
        priorityID: null,
        dueDate: null,
        listTags: [],
        listWatchersIDs: [],
        listAtMentionIDs: [],
        listFileUploadIDs: [],
        listJobAppointeeMappings: [],
        listCustomFieldValues: [],
      } as IConvosInputCreate,

      isLoading: false,

      // Options
      attachments: [] as IAttachment[],
      statusOptions: [] as IStatusForSelection[],
      priorityOptions: [] as IPriorityLevelForSelection[],
      assigneesOptions: [] as IPersonWithEmail[],
      tagsOptions: [] as string[],
      watchersOptions: [] as IPersonWithEmail[],
      mentionOptions: [] as IAtMentionUser[],
      savedReplyOptions: [] as IConvertedSavedReply[],
      customFieldOptions: [] as ICustomField[],
      replySignature: '',

      jobs: [] as IJob[],
      assigneeMandates: [] as IJobAssigneeMandate[],

      // Reply var constants
      replyVar_customerName: '{CustomerName}',
      replyVar_customerFirstName: '{CustomerFirstName}',
      replyVar_customerLastName: '{CustomerLastName}',

      messageHasCustomerName: false,
      messageHasCustomerFirstName: false,
      messageHasCustomerLastName: false,

      replyVariableReplacements: null as IConvosOutputReplyVariableReplacements,
      showReplyVariablesModal: false,
    }
  },

  head: {
    title: 'New Conversation',
  },

  computed: {
    pickerSize(): string {
      return this.$screen.isTabletOrMobile(this.$mq) ? 'medium' : 'small'
    },
    mailboxID(): number {
      return Number(this.$route.params.id)
    },
    hasJobs(): boolean {
      return this.jobs.length > 0
    },
    currentAssignee(): IPersonWithEmail {
      let result = null
      if (this.hasJobs) {
        /* Manually grab the correct assignee for the emitted task */
        const assigneeMandate = this.assigneeMandates.find(
          am => am.statusID === this.formData.statusID,
        )

        if (assigneeMandate) {
          const appointeeMapping = this.formData.listJobAppointeeMappings.find(
            jam => jam.jobID === assigneeMandate.jobID,
          )
          result = this.assigneesOptions.find(
            ao => ao.id === appointeeMapping.appointeeID,
          )
        }
      } else if (this.formData.assigneeID) {
        result = this.assigneesOptions.find(
          a => a.id === this.formData.assigneeID,
        )
      }
      return result
    },
    currentStatus(): IStatusForSelection {
      if (this.formData.statusID) {
        return this.statusOptions.find(s => s.id === this.formData.statusID)
      }
      return null
    },
    jobInfo(): IJobInfo {
      if (!this.hasJobs) {
        return null
      }

      const jobAppointees = this.jobs.map(j => ({
        job: j,
        appointee: this.getPotentialAssigneeByID(
          this.formData.listJobAppointeeMappings.find(jam => jam.jobID === j.id)
            .appointeeID,
        ),
      }))

      return {
        jobAppointees,
        assigneeMandates: this.assigneeMandates,
        currentAssignee: this.currentAssignee,
        currentStatus: this.currentStatus,
      }
    },
    requiresReplyVarReplacement(): boolean {
      return (
        this.messageHasCustomerName ||
        this.messageHasCustomerFirstName ||
        this.messageHasCustomerLastName
      )
    },
    showCreationDeniedOverlay(): boolean {
      if (this.isLoading || this.newConvosCheck.canCreateConversations) {
        return false
      }
      // Direct-connection mailboxes don't use auto-forwarding / outgoing-email
      // setup, so don't show the "complete mailbox setup" popup for them.
      if (this.isDirectConnection) {
        return false
      }
      return true
    },
  },

  watch: {
    'formData.subject': {
      handler() {
        this.handleSubjectMessageChange()
      },
    },
    'formData.message': {
      handler() {
        this.handleSubjectMessageChange()
      },
    },
  },

  async created() {
    this.isLoading = true

    try {
      await Promise.all([
        this.fetchMailboxData(),
        this.fetchOptions(),
        this.fetchCreateNewConversationsCheck(),
      ])
      this.hydrateFieldsFromLocalStorage()
      this.defaultRequiredFields()
      this.resetJobAppointeeMappings()
      this.resetWatchers()
    } catch (error) {
      this.$error.emit(error)
    } finally {
      this.isLoading = false
    }
  },

  mounted() {
    // Pre-seed data from the route.params to seed the breadcrumb listing picker title before the API call...
    if (RouteAssistant.routeState(this.$route, 'mailboxName')) {
      this.mailboxName = RouteAssistant.routeState(this.$route, 'mailboxName')
    }
  },

  methods: {
    handleSubjectMessageChange() {
      const storage = {
        title: this.formData.subject,
        description: this.formData.message,
      } as ICreateItemStorage
      LocalStore.setConvoCreateData(this.mailboxID, storage)
    },

    defaultRequiredFields() {
      this.formData.statusID = this.statusOptions[0].id
      this.formData.priorityID = this.priorityOptions[0].id
    },
    getPotentialAssigneeByID(id?: number): IPersonWithEmail {
      if (id) {
        return this.assigneesOptions.find(ao => ao.id === id)
      }
      return null
    },
    resetJobAppointeeMappings() {
      this.formData.listJobAppointeeMappings = []
      this.jobs.forEach(j => {
        this.formData.listJobAppointeeMappings.push({
          jobID: j.id,
          appointeeID: null,
        })
      })
    },
    async fetchMailboxData() {
      const response = await MailboxesAPI.get_Detail(this.mailboxID)
      this.mailboxName = response.name
      this.replySignature = response.replySignature
      this.isDirectConnection = response.isDirectConnection
    },
    async fetchOptions() {
      const response = await ConversationsAPI.get_FilterOptions(this.mailboxID)

      this.statusOptions = response.listStatuses
      this.priorityOptions = response.listPriorities
      this.watchersOptions = response.listWatchers
      this.assigneesOptions = response.listAssignees
      this.tagsOptions = response.listTags
      this.mentionOptions = response.listAtMentionUsers
      this.savedReplyOptions = response.listSavedReplies
      this.customFieldOptions = response.listCustomFields

      this.jobs = response.listJobs
      this.assigneeMandates = response.listAssigneeMandates
    },

    async fetchCreateNewConversationsCheck() {
      this.newConvosCheck = await MailboxesAPI.get_CreateNewConversationsCheck(
        this.mailboxID,
      )
    },
    clearLocalStorageData() {
      LocalStore.clearConvoCreateData(this.mailboxID)
    },
    resetWatchers() {
      if (this.formData.listWatchersIDs.length === 0) {
        this.formData.listWatchersIDs = [this.$permissions.loggedInUser.id]
      }
    },
    /* Only persisting for subject and message */
    hydrateFieldsFromLocalStorage() {
      try {
        const savedFormData = LocalStore.getConvoCreateData(this.mailboxID)
        if (savedFormData) {
          this.formData.subject = savedFormData.title.toString()
          // Quill will scrub sql injection
          this.formData.message = savedFormData.description.toString()
        }
      } catch (e) {
        this.clearLocalStorageData()
        this.clearFormFully()
        return
      }
    },

    addFileHandler(files: IAttachment[]) {
      this.attachments = files
      this.formData.listFileUploadIDs = files.map(
        (item: IAttachment) => item.id,
      )
    },

    async removeFileHandler(id: number) {
      try {
        await this.FilesAPI.delete_Delete(id)
        this.attachments = this.attachments.filter(item => item.id !== id)
        this.formData.listFileUploadIDs = this.attachments.map(item => item.id)
      } catch (error) {
        this.$messaging.showGenericError()
      }
    },

    async create(createAnother: boolean) {
      this.clearLocalStorageData()
      this.isSubmitting = true

      try {
        const response = await ConversationsAPI.post_Create(
          this.mailboxID,
          this.formData,
        )

        this.$messaging.showSuccess(response.message)

        if (createAnother) {
          this.clearFormAfterCreation()
          this.scrollToTop()
          // Regrab options in case there were any updates (specifically tags when just adding new ones)
          await this.fetchOptions()
        } else {
          LocalStore.itemDetailBackBtnToRoot = true

          RouteAssistant.goToConversation(
            this.$router,
            this.$route.params.accountId,
            this.mailboxID,
            response.id,
            this.mailboxName,
          )
        }
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
      }
    },

    clearFormAfterCreation() {
      this.formData.subject = ''
      this.formData.to = ''
      this.formData.cc = []
      this.formData.message = ''
      this.formData.listAtMentionIDs = []
      this.formData.listFileUploadIDs = []
      this.formData.listCustomFieldValues = []
      this.resetCustomFieldInputs()
    },

    clearFormFully() {
      this.formData.subject = ''
      this.formData.to = ''
      this.formData.cc = []
      this.formData.message = ''
      this.formData.assigneeID = null
      this.formData.priorityID = null
      this.formData.statusID = null
      this.formData.dueDate = null
      this.formData.listTags = []
      this.formData.listWatchersIDs = []
      this.formData.listAtMentionIDs = []
      this.formData.listFileUploadIDs = []
      this.formData.listJobAppointeeMappings = []
      this.formData.listCustomFieldValues = []

      // Set watchers to current user
      this.resetWatchers()
      this.resetJobAppointeeMappings()
      this.defaultRequiredFields()
      this.resetCustomFieldInputs()

      this.attachments = []

      // Reset focus
      this.refocusSubjectField()
    },

    /* Custom field values are checked client-side against each field's settings before the request goes out */
    validateCustomFieldInputs(): IValidationResult {
      const inputs = this.$refs.customFieldInputs as any
      if (inputs && typeof inputs.validate === 'function') {
        return inputs.validate()
      }
      return { success: true }
    },

    resetCustomFieldInputs() {
      const inputs = this.$refs.customFieldInputs as any
      if (inputs && typeof inputs.reset === 'function') {
        inputs.reset()
      }
    },

    /**
     * send to home page
     */
    sendToHome() {
      RouteAssistant.goToMailbox(
        this.$router,
        this.$route.params.accountId,
        this.$route.params.id,
        this.mailboxName,
      )
    },

    clearFocuses() {
      this.focusSubject = false
      this.focusTo = false
    },

    refocusSubjectField() {
      this.clearFocuses()
      this.$nextTick(() => {
        this.focusSubject = true
      })
    },

    refocusToField() {
      this.clearFocuses()
      this.$nextTick(() => {
        this.focusTo = true
      })
    },

    handleCancelClick() {
      this.clearLocalStorageData()
      this.sendToHome()
    },

    insertMentions(matches: any) {
      this.formData.listAtMentionIDs = matches
    },

    async validateAndSubmit(createAnother: boolean) {
      this.pressedCreateAnother = createAnother
      let errorMsg

      const subjectRequired = this.formData.subject.trim().length === 0
      const customerRequired = this.formData.to.trim().length === 0

      if (!(subjectRequired || customerRequired)) {
        const customFieldsResult = this.validateCustomFieldInputs()

        if (!customFieldsResult.success) {
          this.$messaging.showError(customFieldsResult.message)
          return
        }

        // Check if reply variables need replacing (from saved replies selection)
        this.setReplyVarReplacementRequirements(this.formData.message)

        if (this.requiresReplyVarReplacement) {
          this.isSubmitting = true
          this.replyVariableReplacements = await ConversationsAPI.get_ReplyVariableReplacements(
            this.mailboxID,
            this.formData.to,
          )
          this.showReplyVariablesModal = true
          this.isSubmitting = false
          return
        }

        this.create(createAnother)
        return
      }

      if (subjectRequired && customerRequired) {
        errorMsg = 'Please enter a subject and "To" email.'
        this.refocusSubjectField()
      } else if (subjectRequired) {
        errorMsg = 'Please enter a subject.'
        this.refocusSubjectField()
      } else {
        errorMsg = 'Please enter a "To" email.'
        this.refocusToField()
      }
      this.$messaging.showError(errorMsg)
      this.scrollToTop()
    },

    scrollToTop() {
      window.scrollTo(0, 0)
    },

    /* Reply variables */
    closeReplyVariablesModal() {
      this.showReplyVariablesModal = false
    },

    setReplyVarReplacementRequirements(message: string) {
      this.messageHasCustomerName = message.includes(this.replyVar_customerName)
      this.messageHasCustomerFirstName = message.includes(
        this.replyVar_customerFirstName,
      )
      this.messageHasCustomerLastName = message.includes(
        this.replyVar_customerLastName,
      )
    },

    async replaceVariablesAndResubmit() {
      this.formData.message = this.formData.message
        .replace('{CustomerName}', this.replyVariableReplacements.customerName)
        .replace(
          '{CustomerFirstName}',
          this.replyVariableReplacements.customerFirstName,
        )
        .replace(
          '{CustomerLastName}',
          this.replyVariableReplacements.customerLastName,
        )
      this.closeReplyVariablesModal()
      // Need to add this or else local storage doesn't clear (for some reason), in the create() method.
      await this.$nextTick()
      // this.pressedCreateAnother is carried over from the original submission...
      await this.validateAndSubmit(this.pressedCreateAnother)
    },
  },
})
</script>

<style lang="scss">
.conversation-create {
  .add-convo-form {
    display: flex;
    flex-direction: column;
    height: 100%;

    &__main {
      /* Room for sticky footer */
      @include media-breakpoint-up(md) {
        padding-bottom: 120px;
      }

      .subject {
        margin: 10px 0 30px 0;
      }

      .to-cc-wrapper {
        margin-bottom: 20px;
      }

      .meta-section {
        margin-top: 20px;
        padding: 2px 14px;
        border-radius: 8px;
        border: $border--dotted-medium;

        @include media-breakpoint-down(sm) {
          border: none;
          padding: 0;
        }

        > div {
          display: flex;
          align-items: center;
          padding: 12px 0;
          border-bottom: $border--dotted-light;
          @include flex-row-horizontal-spacing(8px);

          .label {
            @include lettering--p-sm;
            font-weight: $bold;
            min-width: 150px;
            max-width: 150px;
            @include single-line-ellipsis;

            > .ddi {
              color: var(--color-text-third);
              padding-right: 4px;
            }

            @include media-breakpoint-down(sm) {
              min-width: 100px;
              max-width: 100px;
            }
          }

          &.small-pad {
            padding: 5px 0;
          }

          .tags-input {
            left: -8px;
            position: relative;
            width: 100%;
          }

          &:last-child {
            border-bottom: none;
          }
        }
      }
    }

    &__footer {
      display: flex;
      padding: 20px 0;
      flex-direction: row;
      justify-content: flex-start;
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      padding: 20px 30px;
      background-color: var(--color-general-white);
      border-top: solid 1px $silver-two;
      /* Above editor */
      z-index: 2;

      button {
        &:first-child {
          margin-right: 14px;
        }
      }

      @include media-breakpoint-down(sm) {
        position: static;
        border: none;
        flex-direction: column;
        padding: 0;
        margin-top: 40px;

        button {
          width: 100%;
          margin-bottom: 14px;
        }
      }
    }
  }

  &__creation-denied-overlay {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: fixed;
    z-index: 9000;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(1, 54, 61, 0.4); // $color--gray-90

    > div {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background-color: var(--color-background-header);
      border: $border--solid-extra-light;
      border-radius: 8px;
      padding: 20px;
      max-width: 500px;
      text-align: center;

      @include media-breakpoint-down(xs) {
        max-width: 90%;
      }

      img {
        width: 80px;
        margin-bottom: 10px;
      }

      .actions-container {
        display: flex;
        justify-content: center;
        margin-top: 20px;

        > *:first-child {
          margin-right: 10px;
        }
      }

      .help-notice {
        margin-top: 20px;
        @include lettering--p-sm;
      }
    }
  }
}
</style>
